// ══════════════════════════════════════════════════════════════════════════════
//  Toolbar y tabla: SearchInput, FilterChip, PageToolbar, Table/Th/Td/Tr/Tf (canon de tablas), EntityCard, Skeleton y TableSkeleton.
// ══════════════════════════════════════════════════════════════════════════════
import { createContext, forwardRef, useContext, useMemo } from 'react';
import { C, T, R, E, F, SP, Z } from '../../design.js';
import { Icon } from './Icon.js';
import { IconButton } from './botones.js';

// SearchInput — input de búsqueda con icono prefijado y borde dinámico.
//   value/onChange esperan callback con valor (no event).
//   width: número (px) o string. Si se omite, ocupa flex 1 con maxWidth 280.
//   `soft` (2026-09-17, pedido del dueño para las carpetas: "darle el diseño
//   calma a la barra de buscar tal cual se había dibujado"): el buscador RELLENO
//   del lenguaje en calma — fondo `surfaceSunken`, sin borde visible, radio
//   `R.lg` y 30 px de alto, la misma altura que el filtro que va a su derecha.
//   Al enfocarse vuelve a blanco (lo hace la clase `gb-field-soft` de gb-base).
const SearchInput = ({ value, onChange, placeholder = "Buscar…", width, autoFocus = false, soft = false, style: sx = {} }) => (
  <div style={{
    position: "relative",
    ...(width != null ? { width } : { flex: "1 1 200px", maxWidth: 280 }),
    ...sx,
  }}>
    <span style={{
      position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
      display: "flex", alignItems: "center", pointerEvents: "none",
    }}>
      <Icon name="search" size={13} color={C.textMuted}/>
    </span>
    <input
      type="text"
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      className={soft ? "gb-buscador gb-i gb-focus gb-field-soft" : "gb-buscador gb-i gb-focus"}
      style={soft ? {
        width: "100%",
        padding: "6px 10px 6px 31px",
        fontSize: 13, color: C.text,
        fontFamily: "inherit", boxSizing: "border-box",
      } : {
        width: "100%",
        padding: "8px 10px 8px 32px",
        borderRadius: R.ctl,
        border: `1px solid ${value ? C.accent : C.border}`,
        boxShadow: value ? `0 0 0 2px ${C.accentLight}` : "none",
        fontSize: 13, color: C.text,
        fontFamily: "inherit", background: C.surface, boxSizing: "border-box",
      }}
    />
  </div>
);

// FilterChip — toggle button con badge contador opcional.
const FilterChip = ({ active = false, count = 0, label = "Filtros", icon = "filter", onClick, style: sx = {} }) => (
  <button onClick={onClick}
    className="gb-i gb-press gb-focus"
    style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "7px 12px", borderRadius: R.ctl,
      border: `1px solid ${active ? C.primaryMid : C.border}`,
      background: active ? C.primaryLight : C.surface,
      color: active ? C.primary : C.textSub,
      cursor: "pointer", fontFamily: "inherit",
      fontSize: 12, fontWeight: 600,
      ...sx,
    }}>
    <Icon name={icon} size={13} color={active ? C.primary : C.textSub}/>
    {label}
    {count > 0 && (
      <span style={{
        ...F.num,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: active ? C.primaryFill : C.borderMid,
        color: C.onPrimary, borderRadius: R.lg, padding: "0 7px",
        fontSize: 10, fontWeight: 700, minWidth: 18, height: 16, lineHeight: 1,
      }}>{count}</span>
    )}
  </button>
);

// PageToolbar — wrapper estandarizado de fila superior.
//   Slots: search, filters, counter, actions.  actions se alinea a la derecha.
const PageToolbar = ({ search, filters, counter, actions, style: sx = {} }) => (
  <div style={{
    display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", ...sx,
  }}>
    {search}
    {filters}
    {counter && (
      <span style={{ ...F.num, fontSize: 12, color: C.textMuted, fontWeight: 500, whiteSpace: "nowrap" }}>
        {counter}
      </span>
    )}
    {/* En el telefono las acciones se parten en renglones: sin flexWrap, cuatro pastillas
        desbordaban la portada de Calidad 48 px y toda la pagina se movia de lado (2026-10-23). */}
    {actions && (
      <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", minWidth: 0 }}>
        {actions}
      </div>
    )}
  </div>
);

// paginasVisibles — números de página que dibuja el Paginador: todas si son 7
//   o menos; si no, la primera, la última y las vecinas de la actual, con "…"
//   en cada hueco. Puro (vitest en paginador.test.js).
const paginasVisibles = (paginas, actual) => {
  if (paginas <= 7) return Array.from({ length: paginas }, (_, i) => i + 1);
  const nums = [...new Set([1, paginas, actual - 1, actual, actual + 1])]
    .filter(n => n >= 1 && n <= paginas).sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => { if (i && n - nums[i - 1] > 1) out.push("…"); out.push(n); });
  return out;
};

// Paginador — pie de lista para colecciones largas: acota el scroll a una
//   página de N filas SIN tocar la búsqueda ni los totales. El llamador filtra
//   TODO y dibuja solo el `slice` de la página; aquí solo se navega. Invisible
//   cuando todo cabe en una página.
//   total: filas filtradas · porPagina · pagina (1-based) · onChange(pagina) · unidad: rótulo.
const Paginador = ({ total = 0, porPagina = 50, pagina = 1, onChange, unidad = "registros", style: sx = {} }) => {
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  if (paginas <= 1) return null;
  const actual = Math.min(Math.max(1, pagina), paginas);
  const desde = (actual - 1) * porPagina + 1;
  const hasta = Math.min(total, actual * porPagina);
  const ir = (p) => { if (p !== actual && p >= 1 && p <= paginas) onChange?.(p); };
  return (
    <nav aria-label="Paginación" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)", alignItems: "center", gap: 8, ...sx }}>
      <span style={{ ...F.num, fontSize: 12, color: C.textMuted, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {desde} a {hasta} de {total} {unidad}
      </span>
      {/* Los controles al centro de la ventana: conforme la lista crece, crecen simétricos. */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <IconButton icon="chevron-left" size={28} title="Página anterior" disabled={actual === 1} onClick={() => ir(actual - 1)} />
      {paginasVisibles(paginas, actual).map((n, i) => n === "…"
        ? <span key={`h${i}`} style={{ ...F.num, fontSize: 12, color: C.textMuted, padding: "0 4px" }}>…</span>
        : (
          <button key={n} type="button" onClick={() => ir(n)} aria-current={n === actual ? "page" : undefined}
            className={n === actual ? "gb-focus" : "gb-ghost gb-press gb-focus"}
            style={{ ...F.num, minWidth: 28, height: 28, padding: "0 6px", borderRadius: R.ctl, fontFamily: "inherit", fontSize: 12,
              fontWeight: n === actual ? 700 : 500, cursor: n === actual ? "default" : "pointer",
              border: `1px solid ${n === actual ? C.primaryMid : "transparent"}`,
              background: n === actual ? C.primaryLight : "transparent", color: n === actual ? C.primary : C.textMed }}>
            {n}
          </button>
        ))}
      <IconButton icon="chevron-right" size={28} title="Página siguiente" disabled={actual === paginas} onClick={() => ir(actual + 1)} />
      </div>
    </nav>
  );
};

// Densidad + sticky cascadean por contexto: Table los provee, Th/Td los leen
// (una prop explícita en Th/Td sobreescribe). Default = comportamiento actual.
// `variant`: "default" (tarjeta opaca, cabecera sobre tableHeader) o "carpeta"
//   (la lista de una carpeta sobre el velo traslúcido: cabecera sin relleno,
//   separadores en widgetLinea; ver DESIGN.md, "Carpetas").
const TableCtx = createContext({ density: "comfortable", sticky: false, variant: "default" });
// EDGE_FADE (Obj. C): sombra de borde "scrolling shadows" — aparece SOLO al
//  desbordar horizontalmente; el cover blanco (capa `local`) la oculta en reposo.
//  El cover va de la superficie a transparente (los navegadores actuales interpolan sin gris).
const EDGE_FADE = {
  backgroundImage:
    `linear-gradient(to right, ${C.surface}, transparent),` +
    `linear-gradient(to left, ${C.surface}, transparent),` +
    `radial-gradient(farthest-side at 0 50%, ${E.scrollEdge}, transparent),` +
    `radial-gradient(farthest-side at 100% 50%, ${E.scrollEdge}, transparent)`,
  backgroundPosition: "left center, right center, left center, right center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "40px 100%, 40px 100%, 14px 100%, 14px 100%",
  backgroundAttachment: "local, local, scroll, scroll",
};

// Table primitives — paddings 12×14, body 12px, header 10px uppercase, hover unificado.
//   `striped` aplica zebra automática (clase gb-striped) — sin checks ei%2 inline.
//   `stickyHeader`/`density`/`scrollFade` (Obj. C) son opt-in; default = actual.
// Table es el canon ÚNICO de tabla del proyecto (DESIGN.md, "Tablas"): la lista
//   es una tarjeta (`card`), cabecera Th en caption sobre tableHeader, cuerpo Td
//   de 12 px, zebra con `striped`, hover solo en filas navegables (Tr con
//   onClick) y totales en Tf. `cols` fija anchos por columna (null = fluida:
//   absorbe el sobrante) y activa tableLayout fixed; `fixed` lo activa cuando
//   el colgroup lo pinta el caller. Nadie vuelve a estilar <th>/<td> a mano.
//   `caja` (2026-09-30): por defecto (true) la tabla trae su propia caja con
//   overflowX y la sombra de borde (EDGE_FADE), igual que siempre. Con
//   caja={false} se pinta la <table> sola, sin envoltorio ni EDGE_FADE: es para
//   quien la monta dentro de SU caja que desplaza en los dos ejes. El th sticky
//   se pega a la caja más cercana que desplaza; si es el envoltorio (que solo
//   desplaza de lado y mide todo el alto de la tabla) el encabezado se va con la
//   lista (medido en la rejilla por espécimen: subía 299 de 300 px) y la barra de
//   lado queda al fondo de la tabla. Con `card` la tarjeta recorta (overflow
//   hidden): caja={false} y card juntos solo sirven si la tabla no desborda.
//   El contexto se memoriza: con un objeto nuevo en cada pintura, cada Th/Td/Tr
//   se volvía a pintar aunque su fila no cambiara (al arrastrar el borde de una
//   columna de la carpeta, toda la lista en cada movimiento del cursor).
const Table = ({ children, minWidth, cols, fixed=false, card=false, striped=false, stickyHeader=false, density="comfortable", variant="default", scrollFade=true, caja=true, style: sx = {} }) => {
  const layoutFijo = fixed || (Array.isArray(cols) && cols.length > 0);
  const ctx = useMemo(() => ({ density, sticky: stickyHeader, variant }), [density, stickyHeader, variant]);
  const contenido = (
    <TableCtx.Provider value={ctx}>
      <table className={striped ? "gb-striped" : undefined} style={{
        width: "100%", borderCollapse: "collapse", fontSize: 12,
        ...(minWidth ? { minWidth } : {}),
        ...(layoutFijo ? { tableLayout: "fixed" } : {}),
        ...sx,
      }}>
        {Array.isArray(cols) && cols.length > 0 && (
          <colgroup>{cols.map((w, i) => <col key={i} style={w != null ? { width: w } : undefined}/>)}</colgroup>
        )}
        {children}
      </table>
    </TableCtx.Provider>
  );
  const tabla = caja
    ? <div style={{ overflowX: "auto", ...(scrollFade ? EDGE_FADE : {}) }}>{contenido}</div>
    : contenido;
  return card
    ? <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.card, overflow: "hidden" }}>{tabla}</div>
    : tabla;
};

const Th = ({ children, align = "left", hideMobile = false, sortable = false, sortDir = null, sticky, density, onClick, style: sx = {}, ...rest }) => {
  const ctx = useContext(TableCtx);
  const d = density ?? ctx.density, isSticky = sticky ?? ctx.sticky;
  const carpeta = ctx.variant === "carpeta";
  const pad = d === "compact" ? `${SP.sm}px ${SP.md}px` : (carpeta ? "10px 14px" : "12px 14px");
  const ariaSort = sortDir === "asc" ? "ascending" : sortDir === "desc" ? "descending" : (sortable ? "none" : undefined);
  return (
    <th className={[hideMobile && "r-hide-mobile", sortable && "gb-th-sort", sortDir && "active"].filter(Boolean).join(" ") || undefined}
      aria-sort={ariaSort} onClick={onClick} style={{
        ...T.caption, ...F.num,
        padding: pad, textAlign: align,
        color: C.textMuted, whiteSpace: "nowrap",
        background: carpeta ? "transparent" : C.tableHeader,
        borderBottom: `1px solid ${carpeta ? C.widgetLinea : C.border}`,
        ...(isSticky ? { position: "sticky", top: 0, zIndex: Z.tableHeader } : {}),
        ...sx,
      }} {...rest}>
      {children}{sortable && <Icon name={sortDir === "asc" ? "chevron-up" : "chevron-down"} size={12} color="currentColor" style={{ marginLeft: 5, verticalAlign: "middle", opacity: sortDir ? 1 : 0.5 }} />}
    </th>
  );
};

const Td = ({ children, align = "left", hideMobile = false, density, style: sx = {}, ...rest }) => {
  const ctx = useContext(TableCtx);
  const d = density ?? ctx.density;
  const pad = d === "compact" ? `${SP.sm}px ${SP.md}px` : (ctx.variant === "carpeta" ? "9px 14px" : "12px 14px");
  return (
    <td className={hideMobile ? "r-hide-mobile" : ""} style={{
      ...F.num,
      padding: pad, textAlign: align, verticalAlign: "middle",
      fontSize: 12, color: C.text,
      ...sx,
    }} {...rest}>{children}</td>
  );
};

// Tr — el hover de fila navegable viene de gb-row (solo si recibe onClick).
// El fondo alterno solo se fija inline cuando alt=true, para no pisar el
// hover de la hoja gb-base en las filas base.
// Reenvía el ref al <tr>: el editor de conceptos lo usa para llevar el scroll y el
// foco al renglón recién agregado o al que le falta descripción, y la captura de
// compactación para seguir la fila activa. Sin forwardRef el ref se perdía en silencio.
const Tr = forwardRef(function Tr({ children, onClick, alt = false, style: sx = {}, className = "", ...rest }, ref) {
  const ctx = useContext(TableCtx);
  return (
    <tr ref={ref} onClick={onClick}
      className={[onClick && "gb-row gb-i", alt && "gb-alt", className].filter(Boolean).join(" ") || undefined}
      style={{
        borderBottom: `1px solid ${ctx.variant === "carpeta" ? C.widgetLinea : C.borderLight}`,
        ...sx,
      }}
      {...rest}
    >{children}</tr>
  );
});

// Tf — celda de pie (totales): misma escala que Td, negrita, sobre tableHeader
//   y con línea superior. Sustituye a los <td> de tfoot pintados a mano.
const Tf = ({ children, align = "left", density, style: sx = {}, ...rest }) => {
  const ctx = useContext(TableCtx);
  const d = density ?? ctx.density;
  const pad = d === "compact" ? `${SP.sm}px ${SP.md}px` : "12px 14px";
  return (
    <td style={{
      ...F.num, padding: pad, textAlign: align, fontSize: 12, fontWeight: 700, color: C.text,
      background: C.tableHeader, borderTop: `1px solid ${C.border}`, whiteSpace: "nowrap",
      ...sx,
    }} {...rest}>{children}</td>
  );
};

// EntityCard — tarjeta 44/1fr/auto reutilizable (Concreto, Compactación, etc).
//   icon: ReactNode (típicamente <Icon name="building" size={22}/>)
//   Con onClick es accionable por teclado (role button + Enter/Space).
const EntityCard = ({ icon, title, subtitle, meta, actions, onClick, dim = false, style: sx = {} }) => (
  <div onClick={onClick}
    className={["gb-i", onClick && "gb-lift gb-press gb-focus"].filter(Boolean).join(" ")}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}
    style={{
      background: C.surface,
      border: `1px solid ${dim ? C.borderLight : C.border}`,
      borderRadius: R.card, padding: "14px 18px",
      display: "grid", gridTemplateColumns: "44px 1fr auto",
      gap: 14, alignItems: "center",
      cursor: onClick ? "pointer" : "default",
      opacity: dim ? 0.75 : 1,
      ...sx,
    }}
  >
    <div style={{
      width: 44, height: 44, borderRadius: R.lg,
      background: dim ? C.borderLight : C.primaryLight,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{
        fontWeight: 700, fontSize: 14, color: dim ? C.textMuted : C.text,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{title}</div>
      {subtitle && (
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{subtitle}</div>
      )}
      {meta && (
        <div style={{
          fontSize: 11, color: C.textMuted, marginTop: 3,
          display: "flex", gap: 12, flexWrap: "wrap",
        }}>{meta}</div>
      )}
    </div>
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
      {actions}
    </div>
  </div>
);

// Skeleton — placeholder de carga; respira con el mismo barrido del LogoLoader
// (clase gb-skel de la hoja gb-base). Inerte para lectores de pantalla.
//   <Skeleton width={120} height={12} />
const Skeleton = ({ width = "100%", height = 12, radius, style: sx = {} }) => (
  <div className="gb-skel" aria-hidden="true"
    style={{ width, height, borderRadius: radius ?? R.sm, ...sx }} />
);

// TableSkeleton — silueta de tabla en carga; respeta paddings y nº de columnas.
const TableSkeleton = ({ rows = 6, cols = 4, minWidth, style: sx = {} }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", ...(minWidth ? { minWidth } : {}), ...sx }}>
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r} style={{ borderBottom: `1px solid ${C.borderLight}` }}>
            {Array.from({ length: cols }).map((_, c) => (
              <td key={c} style={{ padding: "12px 14px" }}>
                <Skeleton width={c === 0 ? "55%" : "80%"} height={12} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);


export { EntityCard, FilterChip, PageToolbar, Paginador, SearchInput, Skeleton, Table, TableSkeleton, Td, Tf, Th, Tr, paginasVisibles };
